import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { deNom, useBranding } from "../../branding";
import { PROBLEM_CODES, api, isNetworkError, isProblemError, problemCode, useApiMutation } from "../../api";
import { Button, ButtonLink, EmptyState, Field, cx } from "../../components";
import { useLiensTransition, useShake } from "../../motion";
import { ROUTES, useDocumentTitle } from "../../shell";
import styles from "./Auth.module.css";
import {
  BORNES,
  LIBELLES,
  SAISIE_VIDE,
  champRefuse,
  messageInscription,
  normaliserInscription,
  validerInscription,
  versRequete,
} from "./Inscription";
import type { ChampInscription, InscriptionSaisie } from "./Inscription";
import { useCountdown } from "./useCountdown";

type Erreurs = Partial<Record<ChampInscription, string>>;

/**
 * Demande d'inscription d'une entreprise. Aucun mot de passe ici : le compte
 * n'existe qu'après l'accord de l'ESN, qui invite alors le contact. Le serveur
 * répond 202 quoi qu'il sache de l'adresse ; l'écran de confirmation est donc
 * le même pour tous.
 */
export function InscriptionPage() {
  const lien = useLiensTransition();
  const { esn_name, product_short } = useBranding();
  useDocumentTitle(`Créer un compte entreprise · ${product_short}`);
  const [saisie, setSaisie] = useState<InscriptionSaisie>(SAISIE_VIDE);
  const [erreursClient, setErreursClient] = useState<Erreurs>({});
  // Le pot de miel : un champ que le formulaire cache et qu'un humain ne
  // remplit jamais. Sa valeur part telle quelle ; le serveur en fait ce qu'il
  // veut sans le dire.
  const [fax, setFax] = useState("");
  const [envoye, setEnvoye] = useState(false);
  const alerte = useRef<HTMLParagraphElement>(null);
  const confirmation = useRef<HTMLDivElement>(null);
  const { ref: formulaire, shake } = useShake<HTMLFormElement>();

  const depot = useApiMutation((s: InscriptionSaisie, f: string) => api.requestSignup(versRequete(s, f)));
  const retryAfter = isProblemError(depot.error) ? depot.error.retryAfter : undefined;
  const restant = useCountdown(retryAfter);

  const champServeur = champRefuse(depot.error);
  const erreurs: Erreurs = { ...erreursClient };
  if (champServeur !== null && depot.error !== null) {
    erreurs[champServeur] = messageInscription(depot.error, restant);
  }
  const alerteGenerale = depot.error !== null && champServeur === null ? messageInscription(depot.error, restant) : null;
  const dejaInscrit = problemCode(depot.error) === PROBLEM_CODES.emailTaken;

  // Le premier champ refusé prend le focus, ou l'alerte quand rien n'est
  // nommé : un lecteur d'écran l'annonce, un clavier ne repart pas du haut.
  useEffect(() => {
    if (Object.keys(erreursClient).length > 0 || champServeur !== null) {
      formulaire.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
    } else if (alerteGenerale !== null) {
      alerte.current?.focus();
    }
  }, [erreursClient, champServeur, alerteGenerale, formulaire]);

  useEffect(() => {
    if (envoye) {
      confirmation.current?.focus();
    }
  }, [envoye]);

  function poser(champ: ChampInscription, valeur: string) {
    setSaisie((s) => ({ ...s, [champ]: valeur }));
    if (erreursClient[champ] !== undefined) {
      setErreursClient((e) => {
        const suite = { ...e };
        delete suite[champ];
        return suite;
      });
    }
  }

  async function soumettre(e: FormEvent) {
    e.preventDefault();
    const normalisee = normaliserInscription(saisie);
    const refus = validerInscription(normalisee);
    setErreursClient(refus);
    if (Object.keys(refus).length > 0) {
      shake();
      return;
    }
    const resultat = await depot.run(normalisee, fax);
    if (resultat === null) {
      if (!isNetworkError(depot.error)) {
        shake();
      }
      return;
    }
    setEnvoye(true);
  }

  if (envoye) {
    return (
      <div ref={confirmation} tabIndex={-1}>
        <EmptyState
          icon="✉️"
          title="Demande reçue"
          description="L'équipe vous contacte au numéro indiqué pour valider votre compte. Vous recevrez ensuite une invitation par e-mail pour choisir votre mot de passe."
          action={
            <ButtonLink to={ROUTES.accueil} variant="secondary">
              Retour à l'accueil
            </ButtonLink>
          }
        />
      </div>
    );
  }

  const desactive = depot.pending;
  const champ = (
    nom: ChampInscription,
    props: { type?: string; autoComplete?: string; inputMode?: "email" | "tel" | "url" | "numeric"; hint?: string; required?: boolean; pleineLargeur?: boolean },
  ) => (
    <Field
      key={nom}
      label={LIBELLES[nom]}
      hint={props.hint}
      error={erreurs[nom]}
      required={props.required}
      className={cx(props.pleineLargeur && styles.pleineLargeur)}
    >
      {(commande) => (
        <input
          {...commande}
          type={props.type ?? "text"}
          autoComplete={props.autoComplete}
          inputMode={props.inputMode}
          spellCheck={false}
          maxLength={nom === "siret" ? 20 : BORNES[nom]}
          value={saisie[nom]}
          onChange={(e) => poser(nom, e.target.value)}
          disabled={desactive}
        />
      )}
    </Field>
  );

  return (
    <>
      <div>
        <h1 className={styles.titre}>Créer un compte entreprise</h1>
        <p className={styles.intro}>
          Réservé aux entreprises clientes {deNom(esn_name)}. Vous déposez une demande ; l'équipe vous
          rappelle, valide votre compte et vous invite comme administrateur. Aucun mot de passe à choisir maintenant.
        </p>
      </div>

      <form ref={formulaire} className={styles.formulaire} onSubmit={(e) => void soumettre(e)} noValidate>
        {alerteGenerale !== null && (
          <p ref={alerte} className={styles.alerte} role="alert" tabIndex={-1}>
            {alerteGenerale}{" "}
            {dejaInscrit && <Link to={ROUTES.connexion} {...lien(ROUTES.connexion, "cube-arriere")}>Se connecter</Link>}
          </p>
        )}

        <div className={styles.grilleChamps}>
          {champ("entreprise", { autoComplete: "organization", required: true, pleineLargeur: true })}
          {champ("contact", { autoComplete: "name", required: true })}
          {champ("fonction", { autoComplete: "organization-title", required: true, hint: "Par exemple « DSI » ou « Responsable achats »." })}
          {champ("email", { type: "email", autoComplete: "email", inputMode: "email", required: true, hint: "Elle deviendra celle de votre compte administrateur." })}
          {champ("telephone", { type: "tel", autoComplete: "tel", inputMode: "tel", required: true, hint: "C'est à ce numéro que l'équipe vous rappelle." })}
          {champ("siret", { inputMode: "numeric", hint: "Facultatif. 14 chiffres." })}
          {champ("site", { type: "url", autoComplete: "url", inputMode: "url", hint: "Facultatif." })}
          <Field label={LIBELLES.besoin} error={erreurs.besoin} required className={styles.pleineLargeur} hint="Expertises recherchées, volume, échéance : ce qui aide l'équipe à préparer l'appel.">
            {(commande) => (
              <textarea
                {...commande}
                rows={5}
                maxLength={BORNES.besoin}
                value={saisie.besoin}
                onChange={(e) => poser("besoin", e.target.value)}
                disabled={desactive}
              />
            )}
          </Field>
        </div>

        {/* Pot de miel : hors flux visuel et hors tabulation, ignoré par
            l'assistance technique. Un robot qui remplit tout le remplit. */}
        <div className="lde-visually-hidden" aria-hidden="true">
          <label htmlFor="inscription-fax">Fax</label>
          <input
            id="inscription-fax"
            name="fax"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={fax}
            onChange={(e) => setFax(e.target.value)}
          />
        </div>

        <Button type="submit" variant="primary" fullWidth loading={depot.pending} disabled={restant > 0}>
          {restant > 0 ? `Réessayer dans ${restant} s` : "Envoyer la demande"}
        </Button>

        <div className={styles.liens}>
          <Link to={ROUTES.connexion} {...lien(ROUTES.connexion, "cube-arriere")}>Déjà un compte ? Se connecter</Link>
          <Link to={ROUTES.accueil}>Retour à l'accueil</Link>
        </div>
      </form>
    </>
  );
}
